import React, { useState, forwardRef, useImperativeHandle } from "react";
import {
  Dialog,
  Typography,
  Box,
  Avatar,
  Divider,
  TextField,
} from "@mui/material";
import { formatPrice, formatDuration } from "../../utils";
import { format } from "date-fns";
import { ptBR } from "date-fns/locale";
import Button from "../button";
import ModalCloseButton from "../modal-close-button";
import { Service, Professional, Client } from "../../@data";
import { createClient } from "../../services/clients";
import {
  createAppointment,
  isTimeSlotAvailable,
} from "../../services/appointments";
import { convertDateToISO } from "../../utils/dateUtils";
import { dateTimeToTimestamp } from "../../utils";
import { removePhoneMask } from "../../utils/masks";
import { useAuth, useNotification, useWorkingHours } from "../../contexts";
import { useNavigate } from "react-router-dom";
import {
  StyledDialogTitle,
  StyledDialogContent,
  StyledDialogActions,
} from "../../styles/modal-styles";

interface AppointmentData {
  service: Service;
  professional: Professional;
  selectedDay: string;
  selectedTime: string;
  selectedProfessionalId: string;
  watchedValues: {
    name: string;
    phone: string;
    birthDate: string;
  };
  client: Partial<Client> | null;
  /** true quando o cliente aceitou a oferta de sobrancelha */
  eyebrowUpsellAccepted?: boolean;
}

export interface AppointmentConfirmationModalRef {
  open: (data: AppointmentData) => void;
  close: () => void;
}

interface IAppointmentConfirmationModalProps {}

const AppointmentConfirmationModal = forwardRef<
  AppointmentConfirmationModalRef,
  IAppointmentConfirmationModalProps
>((_props, ref) => {
  const navigate = useNavigate();
  const { setClient } = useAuth();
  const { handleNotification } = useNotification();
  const { workingHours } = useWorkingHours();

  const [isSubmitting, setIsSubmitting] = useState(false);
  const [appointmentData, setAppointmentData] =
    useState<AppointmentData | null>(null);
  const [observation, setObservation] = useState("");
  const open = !!appointmentData;

  useImperativeHandle(ref, () => ({
    open: (data: AppointmentData) => {
      setAppointmentData(data);
      setObservation(""); // Limpa a observação quando abre a modal
    },
    close: () => {
      setAppointmentData(null);
      setObservation(""); // Limpa a observação quando fecha a modal
    },
  }));

  const handleClose = () => {
    if (!isSubmitting) {
      setAppointmentData(null);
      setObservation(""); // Limpa a observação quando fecha a modal
    }
  };

  const formatAppointmentDay = (date: string) => {
    if (!date) return "";
    const dateTime = new Date(`${date}T12:00`);
    return format(dateTime, "EEEE, dd 'de' MMMM", { locale: ptBR });
  };

  const formatAppointmentTime = (time: string) => (time ? `às ${time}` : "");

  const handleConfirmAppointment = async () => {
    if (!appointmentData) {
      console.error("Dados de agendamento não disponíveis");
      return;
    }

    const {
      service,
      professional,
      selectedDay,
      selectedTime,
      selectedProfessionalId,
      watchedValues,
      client,
      eyebrowUpsellAccepted,
    } = appointmentData;

    if (
      !service ||
      !selectedDay ||
      !selectedTime ||
      !selectedProfessionalId ||
      !professional
    ) {
      console.error("Dados de agendamento incompletos");
      return;
    }

    if (!workingHours) {
      handleNotification(
        "error",
        "Horário de funcionamento indisponível. Tente novamente."
      );
      return;
    }

    setIsSubmitting(true);
    try {
      const slotAvailable = await isTimeSlotAvailable(
        professional,
        selectedDay,
        selectedTime,
        workingHours,
        service.duration
      );

      if (!slotAvailable) {
        handleNotification(
          "error",
          "Este horário não está mais disponível! Tente outro horário."
        );
        return;
      }

      let clientId: string;
      let clientData: Partial<Client>;

      if (client?.id) {
        clientId = client?.id;
        clientData = client || ({} as Partial<Client>);
      } else {
        // Cria novo cliente
        const phoneWithoutMask = removePhoneMask(watchedValues.phone || "");
        const birthDateISO = convertDateToISO(watchedValues.birthDate || "");

        clientId = await createClient({
          name: watchedValues.name || "",
          phone: phoneWithoutMask,
          birthDate: birthDateISO,
        });

        clientData = {
          id: clientId,
          name: watchedValues.name || "",
          phone: phoneWithoutMask,
          birthDate: birthDateISO,
          createdAt: Date.now(),
        };
        setClient(clientData as Client);
      }
      let appointmentDataToCreate = {
        timestamp: dateTimeToTimestamp(selectedDay, selectedTime),
        professionalId: selectedProfessionalId,
        clientId,
        serviceId: service.id,
        professional,
        client: clientData as Client,
        service,
      } as any;
      if (observation) appointmentDataToCreate.observation = observation.trim();
      if (eyebrowUpsellAccepted) {
        appointmentDataToCreate.eyebrowUpsellAccepted = true;
      }

      await createAppointment(appointmentDataToCreate);
      handleClose();
      handleNotification("success", "Agendamento criado com sucesso!");
      navigate("/historico");
    } catch (error) {
      console.error(error);
      handleNotification(
        "error",
        "Erro ao criar agendamento. Tente novamente."
      );
    } finally {
      setIsSubmitting(false);
    }
  };

  document.addEventListener("visibilitychange", () => {
    if (document.visibilityState === "hidden") {
      handleClose();
    } else if (document.visibilityState === "visible") {
      handleClose();
    }
  });

  if (!appointmentData) return null;

  return (
    <Dialog
      open={open}
      onClose={handleClose}
      maxWidth="sm"
      fullWidth
      PaperProps={{ sx: { borderRadius: "1.5rem" } }}
      disableEscapeKeyDown={isSubmitting}
    >
      <StyledDialogTitle sx={{ pb: 1 }}>
        Confirmar Agendamento
        <ModalCloseButton onClose={handleClose} disabled={isSubmitting} />
      </StyledDialogTitle>

      <StyledDialogContent>
        <Box>
          <Box sx={{ mt: 2, p: 2, bgcolor: "grey.50", borderRadius: 2 }}>
            {/* Serviço */}
            <Box sx={{ display: "flex", alignItems: "center", mb: 2 }}>
              <Box sx={{ flex: 1 }}>
                <Typography variant="subtitle1" fontWeight={600}>
                  {appointmentData.service.name}
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  {appointmentData.service.description}
                </Typography>
              </Box>
              <Box sx={{ textAlign: "right" }}>
                <Typography variant="body1" color="primary" fontWeight={600}>
                  {formatPrice(appointmentData.service.price)}
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  {formatDuration(appointmentData.service.duration)}
                </Typography>
              </Box>
            </Box>

            <Divider sx={{ my: 2 }} />

            {/* Profissional */}
            <Box sx={{ mb: 2 }}>
              <Box sx={{ display: "flex", alignItems: "center" }}>
                <Avatar
                  src={appointmentData.professional.imageUrl}
                  sx={{ width: "2.5rem", height: "2.5rem", mr: 1 }}
                >
                  {appointmentData.professional.name?.charAt(0)}
                </Avatar>
                <Typography variant="subtitle1" fontWeight={600}>
                  {appointmentData.professional.name}
                </Typography>
              </Box>
              <Box sx={{ flex: 1, mt: 1 }}>
                <Typography variant="body2" color="text.secondary">
                  {appointmentData.professional.description}
                </Typography>
              </Box>
            </Box>

            <Divider sx={{ my: 2 }} />

            <Box
              sx={{
                mb: { xs: 1.5, sm: 2 },
                p: { xs: 1.5, sm: 2.5 },
                bgcolor: "grey.50",
                borderRadius: 2,
                border: "2px solid",
                borderColor: "primary.main",
                textAlign: "center",
              }}
            >
              <Typography
                variant="body2"
                color="text.secondary"
                gutterBottom
                sx={{ fontSize: { xs: "0.75rem", sm: undefined }, mb: { xs: 0.25, sm: 0.5 } }}
              >
                Data e Horário
              </Typography>
              <Typography
                fontWeight={700}
                sx={{
                  lineHeight: 1.2,
                  fontSize: { xs: "1.1rem", sm: "2.125rem" },
                }}
              >
                {formatAppointmentDay(appointmentData.selectedDay)}
              </Typography>
              <Typography
                color="primary"
                fontWeight={700}
                sx={{
                  mt: { xs: 0.25, sm: 0.5 },
                  fontSize: { xs: "1rem", sm: "1.5rem" },
                }}
              >
                {formatAppointmentTime(appointmentData.selectedTime)}
              </Typography>
            </Box>

            <Divider sx={{ my: 2 }} />

            {/* Campo de Observação */}
            <Box sx={{ mb: 2 }}>
              <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
                Observação (opcional)
              </Typography>
              <TextField
                fullWidth
                multiline
                rows={3}
                placeholder="Adicione uma observação sobre o agendamento..."
                value={observation}
                onChange={(e) => setObservation(e.target.value)}
                variant="outlined"
                size="small"
                disabled={isSubmitting}
                sx={{
                  "& .MuiOutlinedInput-root": {
                    borderRadius: 2,
                  },
                }}
              />
            </Box>
          </Box>
        </Box>
      </StyledDialogContent>

      <StyledDialogActions sx={{ p: 3, pt: 1 }}>
        <Button
          onClick={handleClose}
          variant="outlined"
          color="secondary"
          disabled={isSubmitting}
        >
          Cancelar
        </Button>
        <Button
          onClick={handleConfirmAppointment}
          variant="contained"
          color="primary"
          loading={isSubmitting}
          loadingText="Confirmando..."
        >
          Confirmar
        </Button>
      </StyledDialogActions>
    </Dialog>
  );
});

AppointmentConfirmationModal.displayName = "AppointmentConfirmationModal";

export default AppointmentConfirmationModal;
